import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  CheckCircle, XCircle, Search, RefreshCw, ExternalLink, ChevronUp, ChevronDown,
  Shield, BarChart3, Filter, X, Building, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/config';

export default function StatoConfigurazione() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    questura: 'all',
    istat: 'all',
    region: 'all',
    province: 'all',
    city: 'all',
    status: 'all'
  });
  const [filterOptions, setFilterOptions] = useState({ regions: [], provinces: [], cities: [] });

  useEffect(() => { fetchProperties(); }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const res = await axios.get(`${API}/api/properties/configuration-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || [];
      setProperties(data);
      setFilterOptions({
        regions: [...new Set(data.map(p => p.region).filter(Boolean))].sort(),
        provinces: [...new Set(data.map(p => p.province).filter(Boolean))].sort(),
        cities: [...new Set(data.map(p => p.city).filter(Boolean))].sort()
      });
    } catch (err) {
      console.error(err);
      toast.error('Errore nel caricamento immobili');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let result = [...properties];
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(s) || p.city?.toLowerCase().includes(s) ||
        p.province?.toLowerCase().includes(s) || p.region?.toLowerCase().includes(s) ||
        p.responsible_name?.toLowerCase().includes(s)
      );
    }
    if (filters.questura !== 'all') result = result.filter(p => filters.questura === 'yes' ? p.has_questura : !p.has_questura);
    if (filters.istat !== 'all') result = result.filter(p => filters.istat === 'yes' ? p.has_istat : !p.has_istat);
    if (filters.region !== 'all') result = result.filter(p => p.region === filters.region);
    if (filters.province !== 'all') result = result.filter(p => p.province === filters.province);
    if (filters.city !== 'all') result = result.filter(p => p.city === filters.city);
    if (filters.status !== 'all') result = result.filter(p => p.status === filters.status);

    result.sort((a, b) => {
      let av = a[sortField] ?? '', bv = b[sortField] ?? '';
      if (typeof av === 'boolean') { av = av ? 1 : 0; bv = bv ? 1 : 0; }
      if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [properties, searchTerm, filters, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="h-3 w-3 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="h-3 w-3 text-blue-600" /> : <ChevronDown className="h-3 w-3 text-blue-600" />;
  };

  const clearFilters = () => {
    setFilters({ questura: 'all', istat: 'all', region: 'all', province: 'all', city: 'all', status: 'all' });
    setSearchTerm('');
  };

  const activeCount = Object.values(filters).filter(v => v !== 'all').length + (searchTerm ? 1 : 0);
  const stats = {
    total: properties.length,
    questuraOk: properties.filter(p => p.has_questura).length,
    istatOk: properties.filter(p => p.has_istat).length,
    bothOk: properties.filter(p => p.has_questura && p.has_istat).length,
    noneOk: properties.filter(p => !p.has_questura && !p.has_istat).length
  };

  const handleStatusChange = useCallback(async (propertyId, newStatus) => {
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.patch(`${API}/api/properties/${propertyId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setProperties(prev => prev.map(p => p.id === propertyId ? { ...p, status: newStatus } : p));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel cambio stato');
    }
  }, []);

  return (
    <Layout>
      <div className="space-y-4" data-testid="stato-configurazione">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Totale Immobili" value={stats.total} color="blue" />
          <StatCard label="Questura Attiva" value={stats.questuraOk} color="green" icon={<Shield className="h-4 w-4" />} />
          <StatCard label="ISTAT Attivo" value={stats.istatOk} color="green" icon={<BarChart3 className="h-4 w-4" />} />
          <StatCard label="Entrambi OK" value={stats.bothOk} color="emerald" />
          <StatCard label="Nessuno Configurato" value={stats.noneOk} color="red" />
        </div>

        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Stato Configurazione Immobili
                <Badge variant="secondary">{filtered.length}</Badge>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}
                  className={activeCount > 0 ? 'border-blue-500 text-blue-600' : ''} data-testid="toggle-filters-btn">
                  <Filter className="h-4 w-4 mr-1" /> Filtri {activeCount > 0 && `(${activeCount})`}
                </Button>
                <Button variant="outline" size="sm" onClick={fetchProperties} disabled={loading} data-testid="refresh-btn">
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
                </Button>
              </div>
            </div>
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Cerca per nome, citta, provincia, regione, responsabile..."
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
              {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>}
            </div>
            {showFilters && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3" data-testid="filters-panel">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <FilterSelect label="Questura" value={filters.questura} onChange={v => setFilters(f => ({...f, questura: v}))}
                    options={[{v:'yes',l:'Attiva'},{v:'no',l:'Non attiva'}]} testId="filter-questura" />
                  <FilterSelect label="ISTAT" value={filters.istat} onChange={v => setFilters(f => ({...f, istat: v}))}
                    options={[{v:'yes',l:'Attivo'},{v:'no',l:'Non attivo'}]} testId="filter-istat" />
                  <DynFilterSelect label="Regione" value={filters.region} onChange={v => setFilters(f => ({...f, region: v}))}
                    options={filterOptions.regions} testId="filter-region" />
                  <DynFilterSelect label="Provincia" value={filters.province} onChange={v => setFilters(f => ({...f, province: v}))}
                    options={filterOptions.provinces} testId="filter-province" />
                  <DynFilterSelect label="Comune" value={filters.city} onChange={v => setFilters(f => ({...f, city: v}))}
                    options={filterOptions.cities} testId="filter-city" />
                  <FilterSelect label="Stato" value={filters.status} onChange={v => setFilters(f => ({...f, status: v}))}
                    options={[{v:'active',l:'Attivo'},{v:'private',l:'Private'},{v:'terminated',l:'Cessato'}]} testId="filter-status" />
                </div>
                {activeCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600" data-testid="clear-filters-btn">
                    <X className="h-4 w-4 mr-1" /> Rimuovi filtri ({activeCount})
                  </Button>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12"><RefreshCw className="h-8 w-8 animate-spin text-gray-400" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Building className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Nessun immobile trovato</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-y">
                    <tr>
                      {[
                        { field: 'name', label: 'Immobile' },
                        { field: 'city', label: 'Comune' },
                        { field: 'province', label: 'Prov.' },
                        { field: 'region', label: 'Regione' },
                        { field: 'responsible_name', label: 'Responsabile' },
                        { field: 'has_questura', label: 'Questura' },
                        { field: 'has_istat', label: 'ISTAT' },
                        { field: 'status', label: 'Stato' },
                      ].map(col => (
                        <th key={col.field} onClick={() => handleSort(col.field)}
                          className="px-3 py-3 text-left font-medium text-gray-600 cursor-pointer hover:bg-gray-100 whitespace-nowrap">
                          <div className="flex items-center gap-1">{col.label} <SortIcon field={col.field} /></div>
                        </th>
                      ))}
                      <th className="px-3 py-3 text-center font-medium text-gray-600">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filtered.map((prop, i) => (
                      <tr key={prop.id || i} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 font-medium notranslate" translate="no">{prop.name}</td>
                        <td className="px-3 py-2.5 text-xs">{prop.city || '-'}</td>
                        <td className="px-3 py-2.5 text-xs text-center">{prop.province || '-'}</td>
                        <td className="px-3 py-2.5 text-xs">{prop.region || '-'}</td>
                        <td className="px-3 py-2.5 text-xs">{prop.responsible_name || '-'}</td>
                        <td className="px-3 py-2.5 text-center">
                          {prop.has_questura
                            ? <CheckCircle className="h-5 w-5 text-green-500 inline" />
                            : <XCircle className="h-5 w-5 text-red-400 inline" />}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {prop.has_istat
                            ? <CheckCircle className="h-5 w-5 text-green-500 inline" />
                            : <XCircle className="h-5 w-5 text-red-400 inline" />}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <Select value={prop.status || 'active'} onValueChange={(v) => handleStatusChange(prop.id, v)}>
                            <SelectTrigger className={`h-8 w-[110px] text-xs font-medium border ${
                              prop.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
                              prop.status === 'private' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                              prop.status === 'terminated' ? 'bg-red-50 text-red-700 border-red-200' :
                              'bg-gray-50 text-gray-700 border-gray-200'
                            }`} data-testid={`status-select-${i}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Attivo</SelectItem>
                              <SelectItem value="private">Private</SelectItem>
                              <SelectItem value="terminated">Cessato</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <Button variant="outline" size="sm"
                            onClick={() => window.open(`/immobili/${prop.id}`, '_blank')}
                            data-testid={`config-btn-${i}`}>
                            <Settings className="h-3.5 w-3.5 mr-1" />
                            Configura
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function StatCard({ label, value, color, icon }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <Card className={`border ${colors[color] || ''}`}>
      <CardContent className="p-3 flex items-center gap-2">
        {icon}
        <div><p className="text-xs opacity-70">{label}</p><p className="text-lg font-bold">{value}</p></div>
      </CardContent>
    </Card>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs text-gray-500 mb-1 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9" data-testid={testId}><SelectValue placeholder="Tutti" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti</SelectItem>
          {options.map(opt => <SelectItem key={opt.v} value={opt.v}>{opt.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function DynFilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs text-gray-500 mb-1 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9" data-testid={testId}><SelectValue placeholder="Tutti" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti</SelectItem>
          {options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
